import { motion, useReducedMotion } from 'motion/react';
import { ContrastBadge } from '@/components/coupon-banner/contrast-badge';
import { CouponBannerPreview } from '@/components/coupon-banner/coupon-banner-preview';
import {
  couponContrast,
  couponStudioAnchor,
} from '@/components/coupon-banner/coupon-studio';
import { StoreAppScene } from '@/components/coupon-banner/store-app-scene';
import { KorzaStudioStage } from '@/components/korza-studio/korza-studio-stage';
import { useI18nContext } from '@/i18n/i18n-react';
import { formatContrastRatio } from '@/lib/app-editor/color-contrast';
import type { CouponBannerForm } from '@/lib/app-editor/coupon-banner-form';

const LABEL_CLASS =
  'text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#5B6371]';
const VALUE_CLASS = 'text-[13px] font-semibold tabular-nums text-[#15191E]';

/**
 * Coupon banner canvas in the Korza Studio (KorzaStudioStage): an iPhone with
 * the store's app open (its icon and name, a neutral shop window) and the
 * banner as it is being edited, at the top or above the tabs. The pill above
 * spells out the position and the text contrast. On a short stage (a
 * phone's canvas over the sheet) the device zooms in on the banner.
 */
export function CouponStudioPreview({
  form,
  storeName,
  iconUrl,
  onBannerPress,
}: {
  form: CouponBannerForm;
  storeName: string;
  iconUrl: string | null | undefined;
  /** Tap on the banner: back to editing its text. */
  onBannerPress: () => void;
}) {
  const { LL, locale } = useI18nContext();
  const reduceMotion = useReducedMotion() ?? false;
  const contrast = couponContrast(
    form.backgroundColorHex,
    form.foregroundColorHex
  );
  const banner = (
    // Off, the banner is still shown (it is being edited), dimmed.
    <div className={form.isActive ? undefined : 'opacity-[.55]'}>
      <CouponBannerPreview
        bannerText={form.bannerText}
        backgroundColorHex={form.backgroundColorHex}
        foregroundColorHex={form.foregroundColorHex}
        animated={form.animated}
        position={form.position}
        onPress={onBannerPress}
      />
    </div>
  );
  const top = form.position === 'top';

  return (
    <KorzaStudioStage
      testId="coupon-studio"
      label={LL.couponEditor.studio.label()}
      focus={form.position}
      anchor={couponStudioAnchor(form.position)}
      sceneKey="coupon-store-app"
      offLabel={form.isActive ? null : LL.couponEditor.canvas.off()}
      pills={({ clear }) => (
        // Zoomed in on a bottom banner, the pill would sit on the screen.
        <motion.p
          data-testid="coupon-studio-pill"
          initial={false}
          animate={{ opacity: clear ? 1 : 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.3 }}
          aria-hidden={!clear || undefined}
          className="flex h-8 items-center gap-3 rounded-full bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_0_0_1px_rgba(15,23,42,0.06)]"
        >
          <span className={LABEL_CLASS}>
            {LL.couponEditor.studio.position()}
          </span>
          <span className={VALUE_CLASS}>
            {top
              ? LL.appCustomizationPage.couponBanner.top()
              : LL.appCustomizationPage.couponBanner.bottom()}
          </span>
          <span aria-hidden className="h-3.5 w-px bg-[#15191E]/[.12]" />
          <span className={LABEL_CLASS}>
            {LL.couponEditor.studio.contrast()}
          </span>
          <span className={VALUE_CLASS}>
            {formatContrastRatio(contrast.ratio, locale)}
          </span>
          <ContrastBadge readable={contrast.readable} />
        </motion.p>
      )}
    >
      <StoreAppScene
        storeName={storeName}
        iconUrl={iconUrl}
        top={top ? banner : undefined}
        bottom={top ? undefined : banner}
      />
    </KorzaStudioStage>
  );
}
